import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ConfigurationService } from 'src/app/core/config/configuration.service';
import { SamplingLocation } from 'src/app/shared/sample';
import { HttpParamsFromListRequestDTO, ListRequestDTO, ListResponseDTO } from '..';
import { QuestionType } from '../tests/tests.proxy';

@Injectable()
export class OrdersProxy {
    constructor(private http: HttpClient, private configurationService: ConfigurationService) {}

    async get(
        options: ListRequestDTO & {
            practiceId?: string;
            isArchived?: boolean;
            labNumber?: string;
            isInLab?: boolean;
            isLabProcessing?: boolean;
            isResultComplete?: boolean;
            isResultOut?: boolean;
        }
    ): Promise<ListResponseDTO<OrderSummaryDTO>> {
        const url = this.configurationService.APIURL + '/api/orders';
        let params = HttpParamsFromListRequestDTO(options);
        if (options.practiceId) {
            params = params.append('practiceId', String(options.practiceId));
        }
        if (options.isArchived) {
            params = params.append('isArchived', String(options.isArchived));
        }
        if (options.labNumber) {
            params = params.append('labNumber', String(options.labNumber));
        }
        if (options.isInLab) {
            params = params.append('isInLab', String(options.isInLab));
        }
        if (options.isLabProcessing) {
            params = params.append('isLabProcessing', String(options.isLabProcessing));
        }
        if (options.isResultComplete) {
            params = params.append('isResultComplete', String(options.isResultComplete));
        }
        if (options.isResultOut) {
            params = params.append('isResultOut', String(options.isResultOut));
        }

        return this.http
            .get<ListResponseDTO<OrderSummaryDTO>>(url, { params: params })
            .pipe(
                tap((r) =>
                    r.items.forEach((orderDetails) => {
                        const canDelete = orderDetails.status === OrderStatus.InTransit || orderDetails.status === OrderStatus.InLab;
                        orderDetails.canDelete = canDelete;
                    })
                )
            )
            .toPromise();
    }

    async getDetails(practiceId: string, orderId: string): Promise<OrderDetailsDTO> {
        const url = this.configurationService.APIURL + '/api/orders/' + orderId;
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        return await this.http.get<OrderDetailsDTO>(url, { params: params }).toPromise();
    }

    async getResult(practiceId: string, orderId: string, labelTarget: LabelTarget): Promise<OrderResultDTO> {
        const url = this.configurationService.APIURL + '/api/orders/' + orderId + '/result';
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        params = params.append('labelTarget', Number(labelTarget));
        return await this.http.get<OrderResultDTO>(url, { params: params }).toPromise();
    }

    async getResultDebugText(practiceId: string, orderId: string): Promise<OrderDebugResultDTO> {
        const url = this.configurationService.APIURL + '/api/orders/' + orderId + '/result/debug';
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        return await this.http.get<OrderDebugResultDTO>(url, { params: params }).toPromise();
    }

    async getReport(practiceId: string, orderId: string): Promise<Blob> {
        const url = this.configurationService.APIURL + '/api/orders/' + orderId + '/report';
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        return await this.http.get(url, { responseType: 'blob', params: params }).toPromise();
    }

    async create(practiceId: string, createOrderDTO: CreateOrderDTO): Promise<string> {
        const url = this.configurationService.APIURL + '/api/orders';
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        return await this.http.post<string>(url, createOrderDTO, { params: params }).toPromise();
    }

    createReport(practiceId: string, orderId: string, formData: FormData): Observable<any> {
        const url = this.configurationService.APIURL + '/api/orders/' + orderId + '/report';
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        return this.http.post(url, formData, {
            reportProgress: true,
            observe: 'events',
            params: params,
        });
    }

    async update(orderId: string, practiceId: string, updateOrderDTO: UpdateOrderDTO): Promise<any> {
        const url = this.configurationService.APIURL + '/api/orders/' + orderId;
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        return await this.http.put(url, updateOrderDTO, { params: params }).toPromise();
    }

    async delete(practiceId: string, orderId: string): Promise<any> {
        const url = this.configurationService.APIURL + '/api/orders/' + orderId;
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        return await this.http.delete(url, { params: params }).toPromise();
    }

    async createLabNumber(practiceId: string, createLabNumberRequestDTO: CreateLabNumberRequestDTO): Promise<CreateLabNumberResponseDTO> {
        const url = this.configurationService.APIURL + '/api/orders/labNumber';
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        return await this.http.post<CreateLabNumberResponseDTO>(url, createLabNumberRequestDTO, { params: params }).toPromise();
    }

    async archive(practiceId: string, orderId: string): Promise<object> {
        const url = this.configurationService.APIURL + '/api/orders/' + orderId + '/archive';
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        return await this.http.put(url, { archive: true }, { params: params }).toPromise();
    }

    async unArchive(practiceId: string, orderId: string): Promise<object> {
        const url = this.configurationService.APIURL + '/api/orders/' + orderId + '/archive';
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        return await this.http.put(url, { archive: false }, { params: params }).toPromise();
    }

    async labNumberExists(practiceId: string, labNumber: string): Promise<boolean> {
        const url = this.configurationService.APIURL + '/api/orders/any';
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        params = params.append('labNumber', labNumber);
        return await this.http.get<boolean>(url, { params: params }).toPromise();
    }

    async getStats(): Promise<OrderStats> {
        const url = this.configurationService.APIURL + '/api/orders/stats';
        return await this.http.get<OrderStats>(url).toPromise();
    }

    async startLabProcessing(orderIds: string[]): Promise<BlobWrapper> {
        const url = this.configurationService.APIURL + '/api/orders/labProcessing';
        return await this.postAndDownloadBlob(url, orderIds);
    }

    async export(orderIds: string[]): Promise<BlobWrapper> {
        const url = this.configurationService.APIURL + '/api/orders/export';
        return await this.postAndDownloadBlob(url, orderIds);
    }

    async retest(practiceId: string, orderId: string): Promise<any> {
        const url = this.configurationService.APIURL + '/api/orders/' + orderId + '/retest';
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        return await this.http.post(url, {}, { params: params }).toPromise();
    }

    async postAndDownloadBlob(url: string, body: any): Promise<BlobWrapper> {
        const response = await this.http.post(url, body, { observe: 'response', responseType: 'blob' as 'json' }).toPromise();
        const blobResponse = <HttpResponse<Blob>>response;
        const contentDisposition = blobResponse.headers.get('content-disposition');
        const filename = contentDisposition.split(';')[1].split('filename')[1].split('=')[1].trim();
        return <BlobWrapper>{
            blob: blobResponse.body,
            filename: filename,
        };
    }
}

interface BlobWrapper {
    blob: Blob;
    filename: string;
}

export interface CreateOrderDTO {
    sampledOn: string;
    labNumber: string;
    testReference: string;
    boxHasCode: boolean;

    patientReference: string;
    patientDateOfBirth: string;
    patientGender: Gender;

    sampleData: { toothLocation: number; samplingLocation: SamplingLocation }[];
    formData: { questionKey: string; jsonValue: string }[];
}

export interface UpdateOrderDTO {
    patientReference: string;
    patientDateOfBirth: string;
    patientGender: Gender;

    sampleData: { toothLocation: number; samplingLocation: SamplingLocation }[];
    formData: { questionKey: string; jsonValue: string }[];
}

export interface OrderSummaryDTO {
    id: string;
    practiceId: string;
    practiceName: string;
    status: OrderStatus;

    isArchived: boolean;
    labNumber: string;

    testReference: string;
    testName: string;
    testPrimaryColorHex: string;
    testSecondaryColorHex: string;
    patientReference: string;

    report: {
        isRead: boolean;
        isPurged: boolean;
        fileName: string;
        hasMessage: boolean;
    };

    // client-side added
    canDelete?: boolean;
}

export interface OrderDetailsDTO {
    id: string;
    practiceId: string;
    practiceName: string;
    practiceDebtorNumber: string;
    practiceLanguage: string;
    createdOn: Date | string;
    createdByAdmin: boolean;
    status: OrderStatus;

    isArchived: boolean;
    sampledOn: Date | string;
    inLabReceivedOn: Date | string | null;
    labNumber: string;

    test: {
        reference: string;
        name: string;
        primaryColorHex: string;
        secondaryColorHex: string;
    };

    patient: {
        reference: string;
        dateOfBirth: Date | string | null;
        gender: Gender | null;
    };

    report: {
        isRead: boolean;
        isPurged: boolean;
        fileName: string;
        assignees: string[];
        message: string;
    };

    sampleData: { toothLocation: number; samplingLocation: SamplingLocation; samplingLocationDescription: string; pocketDepth: number }[];
    formData: {
        questionKey: string;
        questionLabel: string;
        questionType: QuestionType;
        jsonValue: string;
        valueDescription: string;
    }[];
}

export enum OrderStatus {
    InTransit = 10,
    InLab = 20,
    LabProcessing = 25,
    ResultIncomplete = 27,
    ResultComplete = 28,
    ResultOut = 30,
}

export interface CreateLabNumberRequestDTO {
    testReference: string;
}

export interface CreateLabNumberResponseDTO {
    labNumber: string;
}

export interface OrderStats {
    monthlyStats: MonthlyStat[];
}
export interface MonthlyStat {
    year: number;
    month: number;
    count: number;
}

export enum Gender {
    M = 0,
    V = 1,
    X = 2,
}

export enum LabelTarget {
    Screen = 0,
    Report = 1,
}

export interface ParseInstrumentFileResponseDTO {
    fileName: string;
    experimentId: string;
}

export interface OrderDebugResultDTO {
    debugText: string;
}

export interface OrderResultDTO {
    orderDetails: OrderDetailsDTO;

    result: number;
    conclusion: string;
    advice: string;

    targetResults: TargetResultDTO[];
    roleSlices: RoleSliceDTO[];
}

export interface TargetResultDTO {
    name: string;
    role: TargetRole;
    complex: TargetComplex;
    concentration: number;
    prevalance: Prevalence;
    prevalanceColor: PrevalenceColor;
}

export enum TargetRole {
    Beneficial = 1,
    Opportunist = 2,
    Pathogen = 3,
}

export enum TargetComplex {
    Gray = 1,
    Green = 2,
    Pink = 3,
    Purple = 4,
    Orange = 5,
    Red = 6,
    Yellow = 7,
}

export enum Prevalence {
    BelowDetectionLimit = 1,
    AtDetectionLimit = 2,
    IncreasedSlightly = 3,
    Increased = 4,
    IncreasedStrongly = 5,
    IncreasedVeryStrongly = 6,
}

export enum PrevalenceColor {
    Green = 1,
    Yellow = 2,
    Red = 3,
}

export interface RoleSliceDTO {
    role: TargetRole;
    percentage: number;
}
